import React, { useState, useRef, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { useAuth } from '@/context/AuthContext';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { toast } from 'sonner';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { 
  Share2, 
  Twitter, 
  Facebook, 
  Linkedin, 
  Link2, 
  Check, 
  Loader2,
  ExternalLink,
  Eye,
  Download,
  Stamp
} from 'lucide-react';

// Pinterest icon component
const PinterestIcon = ({ className }) => (
  <svg className={className} viewBox="0 0 24 24" fill="currentColor">
    <path d="M12 0C5.373 0 0 5.373 0 12c0 5.084 3.163 9.426 7.627 11.174-.105-.949-.2-2.405.042-3.441.218-.937 1.407-5.965 1.407-5.965s-.359-.719-.359-1.782c0-1.668.967-2.914 2.171-2.914 1.023 0 1.518.769 1.518 1.69 0 1.029-.655 2.568-.994 3.995-.283 1.194.599 2.169 1.777 2.169 2.133 0 3.772-2.249 3.772-5.495 0-2.873-2.064-4.882-5.012-4.882-3.414 0-5.418 2.561-5.418 5.207 0 1.031.397 2.138.893 2.738.098.119.112.224.083.345l-.333 1.36c-.053.22-.174.267-.402.161-1.499-.698-2.436-2.889-2.436-4.649 0-3.785 2.75-7.262 7.929-7.262 4.163 0 7.398 2.967 7.398 6.931 0 4.136-2.607 7.464-6.227 7.464-1.216 0-2.359-.632-2.75-1.378l-.748 2.853c-.271 1.043-1.002 2.35-1.492 3.146C9.57 23.812 10.763 24 12 24c6.627 0 12-5.373 12-12S18.627 0 12 0z"/>
  </svg>
);

// Utility to add watermark to image
const addWatermark = async (imageBase64, watermarkText = "MockupPro") => {
  return new Promise((resolve) => {
    const img = new Image();
    img.onload = () => {
      const canvas = document.createElement('canvas');
      canvas.width = img.width;
      canvas.height = img.height;
      const ctx = canvas.getContext('2d');
      
      // Draw original image
      ctx.drawImage(img, 0, 0);
      
      // Add watermark
      const fontSize = Math.max(16, img.width / 30);
      ctx.font = `${fontSize}px Arial`;
      ctx.fillStyle = 'rgba(255, 255, 255, 0.7)';
      ctx.strokeStyle = 'rgba(0, 0, 0, 0.5)';
      ctx.lineWidth = 2;
      
      const text = watermarkText;
      const textWidth = ctx.measureText(text).width;
      const x = img.width - textWidth - 20;
      const y = img.height - 20;
      
      ctx.strokeText(text, x, y);
      ctx.fillText(text, x, y);
      
      // Return base64 without data URL prefix
      const watermarkedBase64 = canvas.toDataURL('image/png').split(',')[1];
      resolve(watermarkedBase64);
    };
    img.src = `data:image/png;base64,${imageBase64}`;
  });
};

const ShareModal = ({ 
  isOpen, 
  onClose, 
  creationId, 
  imageBase64, 
  title = "My MockupPro Creation",
  type = "creation"
}) => {
  const { api, user } = useAuth();
  const [loading, setLoading] = useState(false);
  const [shareUrl, setShareUrl] = useState(null);
  const [shareId, setShareId] = useState(null);
  const [copied, setCopied] = useState(false);
  const [customTitle, setCustomTitle] = useState(title);
  const [stats, setStats] = useState(null);
  const [addWatermarkEnabled, setAddWatermarkEnabled] = useState(user?.plan === 'free');
  const [watermarkedImage, setWatermarkedImage] = useState(null);

  const isFreeUser = user?.plan === 'free';

  // Generate watermarked image when modal opens or toggle changes
  useEffect(() => {
    if (isOpen && imageBase64 && addWatermarkEnabled) {
      addWatermark(imageBase64).then(setWatermarkedImage);
    } else {
      setWatermarkedImage(null);
    }
  }, [isOpen, imageBase64, addWatermarkEnabled]);

  const getDisplayImage = () => {
    if (addWatermarkEnabled && watermarkedImage) {
      return watermarkedImage;
    }
    return imageBase64;
  };

  const handleDownloadWithWatermark = () => {
    const imageToDownload = getDisplayImage();
    if (!imageToDownload) return;
    
    const link = document.createElement('a');
    link.href = `data:image/png;base64,${imageToDownload}`;
    link.download = `mockuppro-${addWatermarkEnabled ? 'watermarked-' : ''}${Date.now()}.png`;
    link.click();
    toast.success('Download started');
  };

  const createShareLink = async () => {
    if (shareUrl) return; // Already created
    
    setLoading(true);
    try {
      const response = await api.post('/share/create', {
        creation_id: creationId,
        title: customTitle
      });
      const fullUrl = `${window.location.origin}/share/${response.data.share_id}`;
      setShareUrl(fullUrl);
      setShareId(response.data.share_id);
      toast.success('Share link created!');
    } catch (error) {
      toast.error('Failed to create share link');
    } finally {
      setLoading(false);
    }
  };

  const copyToClipboard = async () => {
    if (!shareUrl) {
      await createShareLink();
    }
    const urlToCopy = shareUrl || `${window.location.origin}/share/${shareId}`;
    await navigator.clipboard.writeText(urlToCopy);
    setCopied(true);
    toast.success('Link copied to clipboard!');
    setTimeout(() => setCopied(false), 2000);
  };

  const trackAndShare = async (platform, shareUrlFn) => {
    if (!shareUrl) {
      await createShareLink();
    }
    
    // Track the click
    try {
      await api.post(`/share/${shareId}/track?platform=${platform}`);
    } catch (e) {
      // Silent fail for tracking
    }
    
    // Open share window
    const url = shareUrl || `${window.location.origin}/share/${shareId}`;
    const shareWindow = shareUrlFn(url, customTitle);
    window.open(shareWindow, '_blank', 'width=600,height=400');
  };

  const socialPlatforms = [
    {
      name: 'Twitter',
      icon: <Twitter className="w-5 h-5" />,
      color: 'hover:bg-[#1DA1F2] hover:text-white',
      shareUrl: (url, text) => 
        `https://twitter.com/intent/tweet?url=${encodeURIComponent(url)}&text=${encodeURIComponent(`Check out my creation on MockupPro! ${text}`)}`
    },
    {
      name: 'Facebook',
      icon: <Facebook className="w-5 h-5" />,
      color: 'hover:bg-[#4267B2] hover:text-white',
      shareUrl: (url) => 
        `https://www.facebook.com/sharer/sharer.php?u=${encodeURIComponent(url)}`
    },
    {
      name: 'Pinterest',
      icon: <PinterestIcon className="w-5 h-5" />,
      color: 'hover:bg-[#E60023] hover:text-white',
      shareUrl: (url, text) => 
        `https://pinterest.com/pin/create/button/?url=${encodeURIComponent(url)}&description=${encodeURIComponent(text)}`
    },
    {
      name: 'LinkedIn',
      icon: <Linkedin className="w-5 h-5" />,
      color: 'hover:bg-[#0A66C2] hover:text-white',
      shareUrl: (url, text) => 
        `https://www.linkedin.com/sharing/share-offsite/?url=${encodeURIComponent(url)}`
    }
  ];

  const getTypeLabel = () => {
    const labels = {
      'image_generate': 'AI Generated Image',
      'mockup_generate': 'Product Mockup',
      'background_generate': 'Custom Background',
      'image_to_prompt': 'Prompt Generation',
      'prompt_enhance': 'Enhanced Prompt'
    };
    return labels[type] || 'Creation';
  };

  return (
    <Dialog open={isOpen} onOpenChange={onClose}>
      <DialogContent className="sm:max-w-md" data-testid="share-modal">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2" style={{ fontFamily: 'Outfit, sans-serif' }}>
            <Share2 className="w-5 h-5" />
            Share Your {getTypeLabel()}
          </DialogTitle>
          <DialogDescription>
            Share your creation on social media to inspire others
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-6 py-4">
          {/* Preview */}
          {imageBase64 && (
            <div className="relative rounded-lg overflow-hidden bg-muted/30 aspect-video flex items-center justify-center">
              <img 
                src={`data:image/png;base64,${getDisplayImage() || imageBase64}`}
                alt="Preview"
                className="max-h-40 object-contain"
              />
              {addWatermarkEnabled && (
                <div className="absolute bottom-2 right-2 bg-background/80 backdrop-blur-sm px-2 py-1 rounded text-xs flex items-center gap-1">
                  <Stamp className="w-3 h-3" />
                  Watermarked
                </div>
              )}
            </div>
          )}

          {/* Watermark Option */}
          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <div className="flex items-center gap-2">
              <Stamp className="w-4 h-4 text-muted-foreground" />
              <div>
                <p className="text-sm font-medium">Add Watermark</p>
                <p className="text-xs text-muted-foreground">
                  {isFreeUser ? 'Recommended for free users' : 'Optional branding'}
                </p>
              </div>
            </div>
            <Switch
              checked={addWatermarkEnabled}
              onCheckedChange={setAddWatermarkEnabled}
              data-testid="watermark-toggle"
            />
          </div>

          {/* Download with watermark */}
          {imageBase64 && (
            <Button 
              variant="outline" 
              className="w-full" 
              onClick={handleDownloadWithWatermark}
              data-testid="download-watermarked-btn"
            >
              <Download className="w-4 h-4 mr-2" />
              Download {addWatermarkEnabled ? 'with Watermark' : 'Image'}
            </Button>
          )}

          {/* Custom Title */}
          <div className="space-y-2">
            <Label htmlFor="share-title">Caption</Label>
            <Input
              id="share-title"
              value={customTitle}
              onChange={(e) => setCustomTitle(e.target.value)}
              placeholder="Add a caption..."
              data-testid="share-title-input"
            />
          </div>

          {/* Social Share Buttons */}
          <div className="space-y-2">
            <Label>Share to</Label>
            <div className="grid grid-cols-4 gap-3">
              {socialPlatforms.map((platform) => (
                <motion.button
                  key={platform.name}
                  whileHover={{ scale: 1.05 }}
                  whileTap={{ scale: 0.95 }}
                  onClick={() => trackAndShare(platform.name.toLowerCase(), platform.shareUrl)}
                  className={`flex flex-col items-center gap-2 p-3 rounded-xl border border-border transition-colors ${platform.color}`}
                  data-testid={`share-${platform.name.toLowerCase()}`}
                >
                  {platform.icon}
                  <span className="text-xs">{platform.name}</span>
                </motion.button>
              ))}
            </div>
          </div>

          {/* Copy Link */}
          <div className="space-y-2">
            <Label>Or copy link</Label>
            <div className="flex gap-2">
              <Input
                value={shareUrl || 'Click to generate share link...'}
                readOnly
                className="flex-1 text-sm"
                data-testid="share-url-input"
              />
              <Button 
                onClick={copyToClipboard}
                disabled={loading}
                data-testid="copy-share-link-btn"
              >
                {loading ? (
                  <Loader2 className="w-4 h-4 animate-spin" />
                ) : copied ? (
                  <Check className="w-4 h-4" />
                ) : (
                  <Link2 className="w-4 h-4" />
                )}
              </Button>
            </div>
          </div>

          {/* Stats (if share exists) */}
          {stats && stats.shared && (
            <div className="flex items-center gap-4 text-sm text-muted-foreground bg-muted/30 rounded-lg p-3">
              <div className="flex items-center gap-1">
                <Eye className="w-4 h-4" />
                <span>{stats.views} views</span>
              </div>
              {Object.keys(stats.clicks).length > 0 && (
                <div className="flex items-center gap-1">
                  <ExternalLink className="w-4 h-4" />
                  <span>{Object.values(stats.clicks).reduce((a, b) => a + b, 0)} clicks</span>
                </div>
              )}
            </div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
};

export default ShareModal;
